import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastContainer } from './components/ui/Toast';
import { CommandPalette } from './components/CommandPalette';
import { LayoutWithSidebar } from './components/LayoutWithSidebar';

// Auth pages - loaded eagerly (entry points)
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { AuthCallback } from './pages/AuthCallback';

// Lazy loaded pages - split into chunks
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const ContractsList = lazy(() => import('./pages/ContractsList').then(m => ({ default: m.ContractsList })));
const ContractUpload = lazy(() => import('./pages/ContractUpload').then(m => ({ default: m.ContractUpload })));
const ContractDetails = lazy(() => import('./pages/ContractDetails').then(m => ({ default: m.ContractDetails })));
const ContractEdit = lazy(() => import('./pages/ContractEdit').then(m => ({ default: m.ContractEdit })));
const ContractsPending = lazy(() => import('./pages/ContractsPending').then(m => ({ default: m.ContractsPending })));
const MyApprovals = lazy(() => import('./pages/MyApprovals').then(m => ({ default: m.MyApprovals })));
const Calendar = lazy(() => import('./pages/Calendar').then(m => ({ default: m.Calendar })));
const InstallmentsCalendar = lazy(() => import('./pages/InstallmentsCalendar').then(m => ({ default: m.InstallmentsCalendar })));
const Obligations = lazy(() => import('./pages/Obligations').then(m => ({ default: m.Obligations })));
const ObligationTemplates = lazy(() => import('./pages/ObligationTemplates').then(m => ({ default: m.ObligationTemplates })));
const UsersManagement = lazy(() => import('./pages/UsersManagement').then(m => ({ default: m.UsersManagement })));
const Members = lazy(() => import('./pages/Members').then(m => ({ default: m.Members })));
const Invitations = lazy(() => import('./pages/Invitations').then(m => ({ default: m.Invitations })));
const Teams = lazy(() => import('./pages/Teams').then(m => ({ default: m.Teams })));
const TeamDetails = lazy(() => import('./pages/TeamDetails').then(m => ({ default: m.TeamDetails })));
const Templates = lazy(() => import('./pages/Templates').then(m => ({ default: m.Templates })));
const TemplateEditor = lazy(() => import('./pages/TemplateEditor').then(m => ({ default: m.TemplateEditor })));
const Clauses = lazy(() => import('./pages/Clauses').then(m => ({ default: m.Clauses })));
const ClauseTemplates = lazy(() => import('./pages/ClauseTemplates').then(m => ({ default: m.ClauseTemplates })));
const UserApprovals = lazy(() => import('./pages/UserApprovals').then(m => ({ default: m.UserApprovals })));
const Workflows = lazy(() => import('./pages/Workflows').then(m => ({ default: m.Workflows })));
const WorkflowForm = lazy(() => import('./pages/WorkflowForm').then(m => ({ default: m.WorkflowForm })));
const WorkflowInstances = lazy(() => import('./pages/WorkflowInstances').then(m => ({ default: m.WorkflowInstances })));
const Reports = lazy(() => import('./pages/Reports').then(m => ({ default: m.Reports })));
const CustomReports = lazy(() => import('./pages/CustomReports').then(m => ({ default: m.CustomReports })));
const OrganizationSettings = lazy(() => import('./pages/OrganizationSettings').then(m => ({ default: m.OrganizationSettings })));
const Notifications = lazy(() => import('./pages/Notifications').then(m => ({ default: m.Notifications })));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings').then(m => ({ default: m.NotificationSettings })));
const EmailTemplates = lazy(() => import('./pages/EmailTemplates').then(m => ({ default: m.EmailTemplates })));
const ManageOrganizations = lazy(() => import('./pages/ManageOrganizations').then(m => ({ default: m.ManageOrganizations })));
const AdvancedSearch = lazy(() => import('./pages/AdvancedSearch').then(m => ({ default: m.AdvancedSearch })));
const UserGuide = lazy(() => import('./pages/UserGuide').then(m => ({ default: m.UserGuide })));
const AuditLog = lazy(() => import('./pages/AuditLog').then(m => ({ default: m.AuditLog })));
const SystemCheck = lazy(() => import('./pages/SystemCheck').then(m => ({ default: m.SystemCheck })));
const PermissionsDebug = lazy(() => import('./pages/PermissionsDebug').then(m => ({ default: m.PermissionsDebug })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-3 text-sm text-muted-foreground">Carregando...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <OrganizationProvider>
            <ToastProvider>
              <BrowserRouter>
                <Routes>
                  {/* Auth pages - loaded eagerly */}
                  <Route path="/" element={<Login />} />
                  <Route path="/login" element={<Navigate to="/" replace />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />

                  {/* Protected Routes with Layout - lazy loaded */}
                  <Route
                    element={
                      <ProtectedRoute>
                        <LayoutWithSidebar />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="/dashboard" element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />
                    <Route path="/contracts" element={<Suspense fallback={<PageLoader />}><ContractsList /></Suspense>} />
                    <Route path="/contracts/new" element={<Suspense fallback={<PageLoader />}><ContractUpload /></Suspense>} />
                    <Route path="/contracts/pending" element={<Suspense fallback={<PageLoader />}><ContractsPending /></Suspense>} />
                    <Route path="/contracts/:id" element={<Suspense fallback={<PageLoader />}><ContractDetails /></Suspense>} />
                    <Route path="/contracts/:id/edit" element={<Suspense fallback={<PageLoader />}><ContractEdit /></Suspense>} />
                    <Route path="/my-approvals" element={<Suspense fallback={<PageLoader />}><MyApprovals /></Suspense>} />
                    <Route path="/calendar" element={<Suspense fallback={<PageLoader />}><Calendar /></Suspense>} />
                    <Route path="/installments" element={<Suspense fallback={<PageLoader />}><InstallmentsCalendar /></Suspense>} />
                    <Route path="/obligations" element={<Suspense fallback={<PageLoader />}><Obligations /></Suspense>} />
                    <Route path="/obligation-templates" element={<Suspense fallback={<PageLoader />}><ObligationTemplates /></Suspense>} />
                    <Route path="/users" element={<Suspense fallback={<PageLoader />}><UsersManagement /></Suspense>} />
                    <Route path="/members" element={<Suspense fallback={<PageLoader />}><Members /></Suspense>} />
                    <Route path="/invitations" element={<Suspense fallback={<PageLoader />}><Invitations /></Suspense>} />
                    <Route path="/teams" element={<Suspense fallback={<PageLoader />}><Teams /></Suspense>} />
                    <Route path="/teams/:id" element={<Suspense fallback={<PageLoader />}><TeamDetails /></Suspense>} />
                    <Route path="/templates" element={<Suspense fallback={<PageLoader />}><Templates /></Suspense>} />
                    <Route path="/templates/:id/edit" element={<Suspense fallback={<PageLoader />}><TemplateEditor /></Suspense>} />
                    <Route path="/clauses" element={<Suspense fallback={<PageLoader />}><Clauses /></Suspense>} />
                    <Route path="/clause-templates" element={<Suspense fallback={<PageLoader />}><ClauseTemplates /></Suspense>} />
                    <Route path="/user-approvals" element={<Suspense fallback={<PageLoader />}><UserApprovals /></Suspense>} />
                    <Route path="/workflows" element={<Suspense fallback={<PageLoader />}><Workflows /></Suspense>} />
                    <Route path="/workflows/new" element={<Suspense fallback={<PageLoader />}><WorkflowForm /></Suspense>} />
                    <Route path="/workflows/:id/edit" element={<Suspense fallback={<PageLoader />}><WorkflowForm /></Suspense>} />
                    <Route path="/workflows/instances" element={<Suspense fallback={<PageLoader />}><WorkflowInstances /></Suspense>} />
                    <Route path="/reports" element={<Suspense fallback={<PageLoader />}><Reports /></Suspense>} />
                    <Route path="/custom-reports" element={<Suspense fallback={<PageLoader />}><CustomReports /></Suspense>} />
                    <Route path="/settings" element={<Suspense fallback={<PageLoader />}><OrganizationSettings /></Suspense>} />
                    <Route
                      path="/notifications"
                      element={
                        <AdminRoute>
                          <Suspense fallback={<PageLoader />}><Notifications /></Suspense>
                        </AdminRoute>
                      }
                    />
                    <Route path="/notifications/settings" element={<Suspense fallback={<PageLoader />}><NotificationSettings /></Suspense>} />
                    <Route
                      path="/email-templates"
                      element={
                        <AdminRoute>
                          <Suspense fallback={<PageLoader />}><EmailTemplates /></Suspense>
                        </AdminRoute>
                      }
                    />
                    <Route path="/organizations" element={<Suspense fallback={<PageLoader />}><ManageOrganizations /></Suspense>} />
                    <Route path="/search" element={<Suspense fallback={<PageLoader />}><AdvancedSearch /></Suspense>} />
                    <Route path="/user-guide" element={<Suspense fallback={<PageLoader />}><UserGuide /></Suspense>} />
                    <Route path="/audit-log" element={<Suspense fallback={<PageLoader />}><AuditLog /></Suspense>} />
                    <Route path="/system-check" element={<Suspense fallback={<PageLoader />}><SystemCheck /></Suspense>} />
                    <Route path="/permissions-debug" element={<Suspense fallback={<PageLoader />}><PermissionsDebug /></Suspense>} />
                  </Route>

                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
                <ToastContainer />
                <CommandPalette />
              </BrowserRouter>
            </ToastProvider>
          </OrganizationProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
